import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarPlus, FilePlus2, Loader2, PackagePlus, PoundSterling, Search, UserPlus, Users, Wrench, X,
} from "lucide-react";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import { inputCls, btnPrimary, gbp2 } from "@/components/portal/shared";

export function Modal({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-label={title}>
      <button aria-label="Close dialog" className="absolute inset-0 bg-navy-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-xl text-navy-900">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full border border-warm-300 p-1.5 hover:bg-warm-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function CustomerPicker({ api, value, onPick, testid }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/leads", { params: { q } }).then((r) => setResults(r.data.leads.slice(0, 6))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);

  if (value)
    return (
      <div className="flex items-center justify-between rounded-lg border border-navy-900/30 bg-warm-100 px-3 py-2.5" data-testid={`${testid}-selected`}>
        <span className="text-sm font-semibold text-navy-900">{value.firstName} {value.lastName} <span className="font-normal text-slate-500">· {value.postcode || "no postcode"}</span></span>
        <button type="button" onClick={() => onPick(null)} aria-label="Change customer" className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
      </div>
    );
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        data-testid={`${testid}-search`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search customer by name, phone, postcode…"
        className={`${inputCls} pl-9`}
      />
      {results.length > 0 && (
        <ul className="absolute z-10 left-0 right-0 mt-1 rounded-xl border border-warm-200 bg-white shadow-lg max-h-56 overflow-y-auto">
          {results.map((l) => (
            <li key={l.id}>
              <button
                type="button"
                data-testid={`${testid}-option-${l.id}`}
                onClick={() => onPick(l)}
                className="w-full text-left px-4 py-2.5 hover:bg-warm-100 text-sm"
              >
                <span className="font-semibold text-slate-900">{l.firstName} {l.lastName}</span>
                <span className="block text-xs text-slate-500">{l.cust_ref} · {l.postcode || "—"} · {l.phone || "—"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrderPicker({ api, value, onPick }) {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
  }, [api]);

  if (value)
    return (
      <div className="flex items-center justify-between rounded-lg border border-navy-900/30 bg-warm-100 px-3 py-2.5" data-testid="order-picker-selected">
        <span className="text-sm font-semibold text-navy-900">{value.ref} <span className="font-normal text-slate-500">· {value.customer}</span></span>
        <button type="button" onClick={() => onPick(null)} aria-label="Change order" className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
      </div>
    );
  const matches = orders.filter((o) =>
    `${o.ref} ${o.customer}`.toLowerCase().includes(q.toLowerCase())).slice(0, 6);
  return (
    <div>
      <input data-testid="order-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order ref or customer…" className={inputCls} />
      {q && (
        <ul className="mt-1 rounded-xl border border-warm-200 bg-white max-h-48 overflow-y-auto">
          {matches.map((o) => (
            <li key={o.id}>
              <button type="button" data-testid={`order-option-${o.id}`} onClick={() => onPick(o)} className="w-full text-left px-4 py-2.5 hover:bg-warm-100 text-sm">
                <span className="font-semibold text-slate-900">{o.ref}</span>
                <span className="text-slate-500"> · {o.customer} · {gbp2(o.total)} · {o.status}</span>
              </button>
            </li>
          ))}
          {matches.length === 0 && <li className="px-4 py-3 text-xs text-slate-400">No orders match.</li>}
        </ul>
      )}
    </div>
  );
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

const Err = ({ error }) => (error ? <p role="alert" className="text-xs text-red-700 mt-2">{error}</p> : null);

export function NewLeadModal({ onClose }) {
  const { api } = usePortalAuth();
  const navigate = useNavigate();
  const { busy, error, run } = useSubmit();
  const [f, setF] = useState({ firstName: "", lastName: "", phone: "", email: "", postcode: "", address1: "", town: "", interest: "", note: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <Modal title="New Lead / Customer" onClose={onClose} testid="modal-new-lead">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(async () => {
            const { data } = await api.post("/leads", { ...f, email: f.email || null });
            navigate(`/portal/customers/${data.id}`);
          });
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="First name *" aria-label="First name" data-testid="nl-firstname" value={f.firstName} onChange={(e) => set("firstName", e.target.value)} className={inputCls} />
          <input placeholder="Last name" aria-label="Last name" data-testid="nl-lastname" value={f.lastName} onChange={(e) => set("lastName", e.target.value)} className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Phone" aria-label="Phone" data-testid="nl-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
          <input type="email" placeholder="Email" aria-label="Email" data-testid="nl-email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
        </div>
        <input placeholder="Address line 1" aria-label="Address line 1" data-testid="nl-address1" value={f.address1} onChange={(e) => set("address1", e.target.value)} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Town" aria-label="Town" data-testid="nl-town" value={f.town} onChange={(e) => set("town", e.target.value)} className={inputCls} />
          <input placeholder="Postcode" aria-label="Postcode" data-testid="nl-postcode" value={f.postcode} onChange={(e) => set("postcode", e.target.value)} className={inputCls} />
        </div>
        <input placeholder="Product interest (e.g. Roller Blinds)" aria-label="Product interest" data-testid="nl-interest" value={f.interest} onChange={(e) => set("interest", e.target.value)} className={inputCls} />
        <textarea rows={2} placeholder="Note (optional)" aria-label="Note" data-testid="nl-note" value={f.note} onChange={(e) => set("note", e.target.value)} className={`${inputCls} resize-none`} />
        <Err error={error} />
        <button type="submit" data-testid="nl-submit" disabled={busy} className={`${btnPrimary} w-full py-3`}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create Record
        </button>
      </form>
    </Modal>
  );
}

export function BookVisitModal({ lead: preset, onClose, onDone }) {
  const { api } = usePortalAuth();
  const { busy, error, run } = useSubmit();
  const [lead, setLead] = useState(preset || null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  return (
    <Modal title="Book Home Visit" onClose={onClose} testid="modal-book-visit">
      <div className="space-y-3">
        {!preset && <CustomerPicker api={api} value={lead} onPick={setLead} testid="bv-customer" />}
        {preset && <p className="text-sm font-semibold text-navy-900">{preset.firstName} {preset.lastName}</p>}
        <div className="grid grid-cols-2 gap-3">
          <input type="date" aria-label="Visit date" data-testid="bv-date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          <input type="time" aria-label="Visit time" data-testid="bv-time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
        </div>
        <Err error={error} />
        <button
          type="button"
          data-testid="bv-submit"
          disabled={!lead || !date || busy}
          onClick={() =>
            run(async () => {
              await api.patch(`/leads/${lead.id}`, { appointment: { date, time } });
              onDone?.();
              onClose();
            })
          }
          className={`${btnPrimary} w-full py-3`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Book Visit
        </button>
      </div>
    </Modal>
  );
}

export function CreateQuoteModal({ onClose }) {
  const { api } = usePortalAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  return (
    <Modal title="Create Quote" onClose={onClose} testid="modal-create-quote">
      <p className="text-xs text-slate-500 mb-3">Pick the customer — you'll build the quote on their record.</p>
      <CustomerPicker api={api} value={lead} onPick={setLead} testid="cq-customer" />
      <button
        type="button"
        data-testid="cq-submit"
        disabled={!lead}
        onClick={() => navigate(`/portal/customers/${lead.id}?tab=quotes`)}
        className={`${btnPrimary} w-full py-3 mt-4`}
      >
        Open Quote Builder
      </button>
    </Modal>
  );
}

export function NewOrderModal({ onClose }) {
  const { api } = usePortalAuth();
  const navigate = useNavigate();
  const { busy, error, run } = useSubmit();
  const [lead, setLead] = useState(null);
  const [desc, setDesc] = useState("");
  const [total, setTotal] = useState("");

  return (
    <Modal title="New Order" onClose={onClose} testid="modal-new-order">
      <div className="space-y-3">
        <CustomerPicker api={api} value={lead} onPick={setLead} testid="no-customer" />
        <input placeholder="What's being made (e.g. 3 × roller blinds)" aria-label="Order description" data-testid="no-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} />
        <input type="number" min="0" step="0.01" placeholder="Order total £" aria-label="Order total" data-testid="no-total" value={total} onChange={(e) => setTotal(e.target.value)} className={inputCls} />
        <p className="text-[11px] text-slate-400">Deposit is calculated automatically from your settings percentage. Invoice is generated with the order.</p>
        <Err error={error} />
        <button
          type="button"
          data-testid="no-submit"
          disabled={!lead || !total || busy}
          onClick={() =>
            run(async () => {
              await api.post("/orders", {
                lead_id: lead.id,
                items: desc.trim() ? [{ description: desc.trim(), qty: 1, price: Number(total) || 0 }] : [],
                total: Number(total) || 0,
              });
              navigate(`/portal/customers/${lead.id}?tab=orders`);
            })
          }
          className={`${btnPrimary} w-full py-3`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create Order
        </button>
      </div>
    </Modal>
  );
}

export function RecordPaymentModal({ order: preset, onClose, onDone }) {
  const { api } = usePortalAuth();
  const { busy, error, run } = useSubmit();
  const [order, setOrder] = useState(preset || null);
  const [type, setType] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Bank transfer");

  const outstanding = order
    ? type === "deposit"
      ? Math.max(0, (order.deposit_required || 0) - (order.deposit_paid || 0))
      : Math.max(0, (order.balance_due || 0) - (order.balance_paid || 0))
    : 0;

  return (
    <Modal title="Record Payment" onClose={onClose} testid="modal-record-payment">
      <div className="space-y-3">
        {!preset && <OrderPicker api={api} value={order} onPick={(o) => { setOrder(o); setAmount(""); }} />}
        {preset && <p className="text-sm font-semibold text-navy-900">{preset.ref} · {preset.customer}</p>}
        {order && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <select aria-label="Payment type" data-testid="rp-type" value={type} onChange={(e) => { setType(e.target.value); setAmount(""); }} className={inputCls}>
                <option value="deposit">Deposit</option>
                <option value="balance">Balance</option>
              </select>
              <select aria-label="Payment method" data-testid="rp-method" value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
                <option>Bank transfer</option>
                <option>Card</option>
                <option>Cash</option>
              </select>
            </div>
            <input type="number" min="0" step="0.01" placeholder={`Amount £ (outstanding ${gbp2(outstanding)})`} aria-label="Amount" data-testid="rp-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
          </>
        )}
        <Err error={error} />
        <button
          type="button"
          data-testid="rp-submit"
          disabled={!order || !Number(amount) || busy}
          onClick={() =>
            run(async () => {
              const amt = Number(amount) || 0;
              await api.patch(`/orders/${order.id}`, type === "deposit"
                ? { deposit_paid: (order.deposit_paid || 0) + amt, deposit_method: method }
                : { balance_paid: (order.balance_paid || 0) + amt, balance_method: method });
              onDone?.();
              onClose();
            })
          }
          className={`${btnPrimary} w-full py-3`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Record Payment
        </button>
      </div>
    </Modal>
  );
}

export function ScheduleInstallModal({ order: preset, onClose, onDone }) {
  const { api } = usePortalAuth();
  const { busy, error, run } = useSubmit();
  const [order, setOrder] = useState(preset || null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [installer, setInstaller] = useState("");
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data.users.filter((u) => u.role === "installer"))).catch(() => {});
  }, [api]);

  return (
    <Modal title="Schedule Installation" onClose={onClose} testid="modal-schedule-install">
      <div className="space-y-3">
        {!preset && <OrderPicker api={api} value={order} onPick={setOrder} />}
        {preset && <p className="text-sm font-semibold text-navy-900">{preset.ref} · {preset.customer}</p>}
        <div className="grid grid-cols-2 gap-3">
          <input type="date" aria-label="Install date" data-testid="si-date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          <input type="time" aria-label="Install time" data-testid="si-time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
        </div>
        {team.length > 0 ? (
          <select aria-label="Installer" data-testid="si-installer" value={installer} onChange={(e) => setInstaller(e.target.value)} className={inputCls}>
            <option value="">Assign installer…</option>
            {team.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
          </select>
        ) : (
          <input placeholder="Installer name" aria-label="Installer" data-testid="si-installer" value={installer} onChange={(e) => setInstaller(e.target.value)} className={inputCls} />
        )}
        <Err error={error} />
        <button
          type="button"
          data-testid="si-submit"
          disabled={!order || !date || busy}
          onClick={() =>
            run(async () => {
              await api.patch(`/orders/${order.id}`, { install_date: date, install_time: time, installer, status: "Installation Booked" });
              onDone?.();
              onClose();
            })
          }
          className={`${btnPrimary} w-full py-3`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Schedule Install
        </button>
      </div>
    </Modal>
  );
}

export function QuickActions({ onDone }) {
  const [modal, setModal] = useState("");
  const close = () => setModal("");
  const actions = [
    ["new-lead", "New Lead", UserPlus],
    ["new-customer", "New Customer", Users],
    ["book-visit", "Book Home Visit", CalendarPlus],
    ["create-quote", "Create Quote", FilePlus2],
    ["new-order", "New Order", PackagePlus],
    ["record-payment", "Record Payment", PoundSterling],
    ["schedule-install", "Schedule Install", Wrench],
  ];
  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="quick-actions">
        {actions.map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            data-testid={`quick-action-${key}`}
            onClick={() => setModal(key)}
            className="inline-flex items-center gap-1.5 rounded-full border border-navy-900/25 bg-white px-4 py-2 text-xs font-semibold text-navy-900 hover:bg-navy-900 hover:text-white transition-colors"
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {(modal === "new-lead" || modal === "new-customer") && <NewLeadModal onClose={close} />}
      {modal === "book-visit" && <BookVisitModal onClose={close} onDone={onDone} />}
      {modal === "create-quote" && <CreateQuoteModal onClose={close} />}
      {modal === "new-order" && <NewOrderModal onClose={close} />}
      {modal === "record-payment" && <RecordPaymentModal onClose={close} onDone={onDone} />}
      {modal === "schedule-install" && <ScheduleInstallModal onClose={close} onDone={onDone} />}
    </>
  );
}
